import { createContext, useEffect, useState } from "react";
import { getCategoriesDropdown } from "../libs/api/categoryApi";
import { getBookCategoriesDropdown } from "../libs/api/bookCategoryApi";
import { getAudioCategoriesDropdown } from "../libs/api/audioCategoryApi";
import { getVideoCategoriesDropdown } from "../libs/api/videoCategoryApi";
import { getHomeSectionCategoriesDropdown } from "../libs/api/homeSectionCategoryApi";
import { getTranslation } from "../utils/i18n";
import { formatNumber } from "../utils/numbers";
import { formatDate, formatDateTime, formatLongDate } from "../utils/dates";

export const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
    // Language state - default is Farsi
    const [language, setLanguageState] = useState(() => {
        const savedLang = localStorage.getItem('app_language');
        return savedLang || 'fa';
    });

    const [categoriesDropdown, setCategoriesDropdown] = useState([]);
    const [bookCategoriesDropdown, setBookCategoriesDropdown] = useState([]);
    const [audioCategoriesDropdown, setAudioCategoriesDropdown] = useState([]);
    const [videoCategoriesDropdown, setVideoCategoriesDropdown] = useState([]);
    const [homeSectionCategoriesDropdown, setHomeSectionCategoriesDropdown] = useState([]);
    const [categorySearch, setCategorySearch] = useState("");
    const [bookCategorySearch, setBookCategorySearch] = useState("");
    const [audioCategorySearch, setAudioCategorySearch] = useState("");
    const [videoCategorySearch, setVideoCategorySearch] = useState("");
    const [homeSectionCategorySearch, setHomeSectionCategorySearch] = useState("");

    const fetchCategoriesDropdown = async () => {
        const response = await getCategoriesDropdown(categorySearch);
        setCategoriesDropdown(response.data);
    }
    const fetchBookCategoriesDropdown = async () => {
        const response = await getBookCategoriesDropdown(bookCategorySearch);
        setBookCategoriesDropdown(response.data);
    }
    const fetchAudioCategoriesDropdown = async () => {
        const response = await getAudioCategoriesDropdown(audioCategorySearch);
        setAudioCategoriesDropdown(response.data);
    }
    const fetchVideoCategoriesDropdown = async () => {
        const response = await getVideoCategoriesDropdown(videoCategorySearch);
        setVideoCategoriesDropdown(response.data);
    }
    const fetchHomeSectionCategoriesDropdown = async () => {
        const response = await getHomeSectionCategoriesDropdown();
        setHomeSectionCategoriesDropdown(response.data);
    }
    useEffect(() => {
        fetchCategoriesDropdown();
    }, [categorySearch]);

    useEffect(() => {
        fetchBookCategoriesDropdown();
        fetchAudioCategoriesDropdown();
        fetchVideoCategoriesDropdown();
        fetchHomeSectionCategoriesDropdown();
    }, []);

    // Language management
    const setLanguage = (lang) => {
        setLanguageState(lang);
        localStorage.setItem('app_language', lang);

        // Update HTML attributes
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';
    };

    // Initialize HTML attributes on mount
    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = language === 'fa' ? 'rtl' : 'ltr';
    }, [language]);

    // Translation function
    const t = (key) => getTranslation(key, language);

    // Number formatting
    const formatNum = (num) => formatNumber(num, language);

    // Date formatting
    const formatDateStr = (date, formatStr = null) => formatDate(date, language, formatStr);
    const formatDateTimeStr = (date) => formatDateTime(date, language);
    const formatLongDateStr = (date) => formatLongDate(date, language);

    // Check if current language is RTL
    const isRTL = language === 'fa';

    return (
        <AppContext.Provider
            value={{
                // Existing values
                categoriesDropdown,
                bookCategoriesDropdown,
                audioCategoriesDropdown,
                videoCategoriesDropdown,
                homeSectionCategoriesDropdown,
                categorySearch,
                setCategorySearch,
                bookCategorySearch,
                setBookCategorySearch,
                audioCategorySearch,
                setAudioCategorySearch,
                videoCategorySearch,
                setVideoCategorySearch,
                homeSectionCategorySearch,
                setHomeSectionCategorySearch,
                fetchCategoriesDropdown,
                fetchBookCategoriesDropdown,
                fetchAudioCategoriesDropdown,
                fetchVideoCategoriesDropdown,
                fetchHomeSectionCategoriesDropdown,
                // Language features
                language,
                setLanguage,
                t,
                formatNum,
                formatDateStr,
                formatDateTimeStr,
                formatLongDateStr,
                isRTL
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
